import type {
  Bookmark,
  ChatModelOption,
  CustomersResponse,
  DictationDetail,
  DictationItem,
  DictationListResult,
  HistoryThread,
  HistoryTurn,
  Me,
  PopularQuestion,
  QueryDone,
  RecentDoc,
  ScheduleCreated,
  ScheduleInput,
  ScheduleItem,
  ScheduleMeta,
  ShareCreated,
  ShareLink,
  SharedConversation,
  SkillResult,
} from "./types";
import { getAnswerOptions } from "@/lib/settings";

const BASE = "/api/chat";

export class UnauthorizedError extends Error {}

/** 403 — 인증은 됐으나 사용 권한 없음(웹채팅 사용자 미등록 등).
 *  code === "CHAT_NOT_REGISTERED" 면 Gate 가 '사용 등록 필요' 안내 화면을 띄운다. */
export class ForbiddenError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

// ── 세션 만료 → 자동 로그오프(중앙 처리) ──
// 서버 세션이 만료되면 어떤 API든 401을 돌려준다. 401을 한 곳에서 받아 앱 내
// 로그인 화면(Gate → <Login/>)으로 전환한다.
// ⚠ 여기서 곧바로 SSO(백엔드 /login → pilgrims)로 리다이렉트하지 않는다.
//   SSO 는 사용자가 로그인 버튼을 눌렀을 때만 시작해야 한다(자동 SSO 방지).
//   따라서 만료 시엔 앱 루트로 보내 로그인 화면만 띄우고, 실제 SSO 는 버튼에서 시작한다.
let _sessionExpired = false;

// 로그아웃 진행 중 플래그 — performLogout 이 (logout → config 조회) await 하는 동안
// SessionGuard 의 idle 인터벌·다른 요청의 401 이 handleSessionExpired 로 reload 를
// 걸면 pilgrims 공유 쿠키로 즉시 재로그인되어 "로그아웃이 안 되는" 경합이 생긴다.
// 로그아웃이 시작되면 만료 네비게이션을 전부 무시하고 로그아웃 리다이렉트만 수행한다.
let _loggingOut = false;

export function isSessionExpired(): boolean {
  return _sessionExpired;
}

// 재검증 진행 중 플래그 — 동시 401 여러 건이 /me 재검증을 중복 실행하지 않도록.
let _verifying = false;

/** 세션 만료 처리(2단계 판정): 개별 요청의 401 은 일시 오류(pilgrims 순단·프록시
 *  재시작 등)일 수 있으므로 곧바로 리로드하지 않고 /me 로 1회 재검증한다.
 *  - /me 200 → 세션 유효(또는 pilgrims 쿠키로 재부트스트랩됨): 아무것도 안 함.
 *  - /me 401 → 진짜 만료: 로그인 화면으로 전환.
 *  - /me 5xx·망오류 → 판정 불가: 리로드하지 않고 다음 요청에서 재판정.
 *  (과거: 401 즉시 window.location.reload() → pilgrims 자동 재로그인과 겹쳐
 *   "가끔 화면이 저절로 새로고침"되던 버그, 2026-07-10 수정) */
export function handleSessionExpired(): void {
  if (_loggingOut || _sessionExpired || _verifying) return;
  _verifying = true;
  fetch(`${BASE}/me`, { credentials: "same-origin" })
    .then((res) => {
      if (res.status === 401) navigateToLogin();
      /* 그 외(200/5xx 등)는 유지 — 세션 유효거나 일시 장애 */
    })
    .catch(() => {
      /* 망 오류 — 판정 불가. 리로드하지 않는다 */
    })
    .finally(() => {
      _verifying = false;
    });
}

/** 진짜 만료 확정 시에만 호출: 앱 루트로 보내 로그인 화면을 띄운다(SSO 자동 시작 금지). */
function navigateToLogin(): void {
  if (_loggingOut) return; // 로그아웃 리다이렉트가 우선 — 경합 방지
  if (_sessionExpired) return;
  _sessionExpired = true;
  // 앱 루트로 이동 → Gate 가 me() 401 을 받아 <Login/> 렌더. SSO 는 버튼 클릭 시에만.
  if (window.location.pathname === "/" && !window.location.search) {
    window.location.reload();
  } else {
    window.location.href = "/";
  }
}

export interface AuthConfig {
  auth_mode: "pilgrims" | "nw" | "disabled";
  login_url: string;
  logout_url: string;
}

/** 완전 로그아웃: ArkiveAI 세션 파기 후, pilgrims 모드면 공유 쿠키까지 만료시키러 이동.
 *  pilgrims 공유 쿠키(pilgrims_auth)는 ArkiveAI 세션만 지워선 사라지지 않아
 *  다음 요청에서 자동 재로그인된다. logout_url 로 이동해 공유 쿠키를 만료시킨다. */
export async function performLogout(): Promise<void> {
  _loggingOut = true; // 이후 handleSessionExpired 네비게이션 무시(경합 방지)
  try {
    await api.logout();
  } catch {
    /* 이미 만료 등 — 무시 */
  }
  let cfg: AuthConfig | null = null;
  try {
    cfg = await api.authConfig();
  } catch {
    /* config 조회 실패 — 로컬 로그아웃만 */
  }
  const returnTo = `${window.location.origin}/`;
  if (cfg?.auth_mode === "pilgrims" && cfg.logout_url) {
    const sep = cfg.logout_url.includes("?") ? "&" : "?";
    window.location.href = `${cfg.logout_url}${sep}return=${encodeURIComponent(returnTo)}`;
    return;
  }
  if (cfg?.auth_mode === "pilgrims") {
    // logout_url 미설정 — 공유 쿠키를 만료시킬 방법이 없다. loginUrl 로 보내면
    // 유효한 pilgrims 쿠키로 즉시 재로그인되므로(자동 SSO) 절대 보내지 않는다.
    console.error(
      "PILGRIMS_LOGOUT_URL 미설정 — 완전 로그아웃 불가. 서버 env 를 확인하세요.",
    );
  }
  // 폴백: 앱 루트로만 이동(자동 SSO 시작 금지). ArkiveAI 세션은 위에서 파기됨.
  window.location.href = "/";
}

async function req<T>(path: string, init?: RequestInit, opts?: { public?: boolean }): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    ...init,
  });
  if (res.status === 401) {
    if (!opts?.public) handleSessionExpired(); // 보호된 호출 → 자동 로그오프
    throw new UnauthorizedError("UNAUTHENTICATED");
  }
  if (res.status === 403) {
    // 미등록 사용자 등 권한 거부 — 리로드/로그오프하지 않고 구분 가능한 에러로 전달.
    let code: string | undefined;
    let msg = "사용 권한이 없습니다.";
    try {
      const j = await res.json();
      code = j?.detail?.code ?? j?.code;
      msg = j?.detail?.message || j?.message || msg;
    } catch {
      /* ignore */
    }
    throw new ForbiddenError(msg, code);
  }
  if (res.status === 429) throw new Error("요청이 너무 잦습니다. 잠시 후 다시 시도해 주세요.");
  if (!res.ok) {
    let msg = `요청 실패 (${res.status})`;
    try {
      const j = await res.json();
      msg = j?.detail?.message || j?.message || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  // 초기 세션 확인은 public 으로 — 401 이어도 자동 SSO 리다이렉트하지 않고
  // UnauthorizedError 만 던진다(Gate 가 받아 로그인 화면 표시). SSO 는 버튼에서만 시작.
  me: () => req<Me>("/me", undefined, { public: true }),
  customers: () => req<CustomersResponse>("/customers"),
  announcement: () =>
    req<{ announcement: { id: number; message: string; level: string } | null }>("/announcement"),
  // 답변 모델 셀렉터 목록(관리자 등록). 미등록이면 items=[] → 셀렉터 숨김.
  models: () => req<{ items: ChatModelOption[] }>("/models"),
  query: (question: string, customer: string, threadId = "", modelId?: number | null) =>
    req<QueryDone>("/query", {
      method: "POST",
      body: JSON.stringify({
        question,
        customer,
        thread_id: threadId,
        model_id: modelId ?? undefined,
        ...getAnswerOptions(),
      }),
    }),
  // 참고문서 일부를 제외(deselect)하고 동일 질문 재생성 (웹 전용·비스트리밍)
  regenerate: (question: string, customer: string, threadId = "", excludeUrls: string[] = []) =>
    req<QueryDone>("/regenerate", {
      method: "POST",
      body: JSON.stringify({
        question,
        customer,
        thread_id: threadId,
        exclude_urls: excludeUrls,
        ...getAnswerOptions(),
      }),
    }),
  confirm: (confirm_token: string) =>
    req<QueryDone>("/confirm", { method: "POST", body: JSON.stringify({ confirm_token }) }),
  decline: (confirm_token: string) =>
    req<QueryDone>("/decline", { method: "POST", body: JSON.stringify({ confirm_token }) }),
  save: (session_id: string) =>
    req<{ status: string; page_url?: string; message?: string }>("/save", {
      method: "POST",
      body: JSON.stringify({ session_id }),
    }),
  feedback: (query_id: string, feedback: 1 | -1, comment = "") =>
    req<{ upserted: boolean }>("/feedback", {
      method: "POST",
      body: JSON.stringify({ query_id, feedback, comment }),
    }),
  suggest: (question: string, answer: string) =>
    req<{ suggestions: string[] }>("/suggest", {
      method: "POST",
      body: JSON.stringify({ question, answer }),
    }),
  upload: async (file: File, threadId = "") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("thread_id", threadId);
    const res = await fetch(`${BASE}/upload`, { method: "POST", credentials: "same-origin", body: fd });
    if (res.status === 401) { handleSessionExpired(); throw new UnauthorizedError("UNAUTHENTICATED"); }
    if (!res.ok) throw new Error(`업로드 실패 (${res.status})`);
    return res.json() as Promise<{ status: string; title?: string; chars?: number; message?: string }>;
  },
  // ── 스킬(회의록·요약·번역·비교 등) — 다중 파일 지원 ──
  skill: async (files: File[], text: string, threadId = "", actionHint = "") => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    fd.append("text", text);
    fd.append("thread_id", threadId);
    fd.append("action_hint", actionHint);
    const res = await fetch(`${BASE}/skill`, { method: "POST", credentials: "same-origin", body: fd });
    if (res.status === 401) { handleSessionExpired(); throw new UnauthorizedError("UNAUTHENTICATED"); }
    if (!res.ok) throw new Error(`스킬 실행 실패 (${res.status})`);
    return res.json() as Promise<SkillResult>;
  },
  // ── 개인 북마크(즐겨찾기) ──
  bookmarkAdd: (b: { query_id?: string; question?: string; answer: string; customer?: string; note?: string }) =>
    req<{ id: number; status: string }>("/bookmark", { method: "POST", body: JSON.stringify(b) }),
  bookmarks: () => req<{ items: Bookmark[] }>("/bookmarks"),
  bookmarkDelete: (id: number) => req<{ status: string }>(`/bookmark/${id}`, { method: "DELETE" }),
  // ── 대화 공유 링크 ──
  shareCreate: (
    title: string,
    turns: { role: "user" | "bot"; text: string }[],
    opts?: { expires_days?: number; require_login?: boolean },
  ) =>
    req<ShareCreated>("/share", {
      method: "POST",
      body: JSON.stringify({
        title,
        turns,
        expires_days: opts?.expires_days ?? 30,
        require_login: opts?.require_login ?? false,
      }),
    }),
  shares: () => req<{ items: ShareLink[] }>("/shares"),
  shareRevoke: (token: string) =>
    req<{ status: string }>(`/share/${encodeURIComponent(token)}`, { method: "DELETE" }),
  shared: (token: string) =>
    req<SharedConversation>(`/shared/${encodeURIComponent(token)}`, undefined, { public: true }), // 공개(무인증) — 401이어도 자동 로그오프 금지
  // ── 첫 화면 위젯 ──
  popular: () => req<{ items: PopularQuestion[] }>("/popular"),
  recentDocs: () => req<{ items: RecentDoc[] }>("/recent-docs"),
  // ── 대화 이력 ──
  history: () => req<{ items: HistoryThread[] }>("/history"),
  historyDetail: (threadId: string) =>
    req<{ thread_id: string; turns: HistoryTurn[] }>(`/history/${encodeURIComponent(threadId)}`),
  historySearch: (q: string) =>
    req<{ items: (HistoryTurn & { thread_id: string })[] }>(`/history/search?q=${encodeURIComponent(q)}`),
  historyDelete: (threadId: string) =>
    req<{ status: string }>(`/history/${encodeURIComponent(threadId)}`, { method: "DELETE" }),
  historyRename: (threadId: string, title: string) =>
    req<{ status: string; thread_id: string; title: string | null }>(
      `/history/${encodeURIComponent(threadId)}`,
      { method: "PATCH", body: JSON.stringify({ title }) },
    ),
  // ── 일정 관리(v3.26) — /api/chat/schedules/* (nginx /api/chat 프록시 재사용) ──
  scheduleMeta: () => req<ScheduleMeta>("/schedules/meta"),
  schedules: (fromIso: string, toIso: string, category?: string, field?: string) => {
    const p = new URLSearchParams({ from: fromIso, to: toIso });
    if (category) p.set("category", category);
    if (field) p.set("field", field);
    return req<{ items: ScheduleItem[] }>(`/schedules?${p.toString()}`);
  },
  scheduleCreate: (b: ScheduleInput) =>
    req<ScheduleCreated>("/schedules", { method: "POST", body: JSON.stringify(b) }),
  scheduleUpdate: (id: number, b: Partial<ScheduleInput>) =>
    req<ScheduleItem>(`/schedules/${id}`, { method: "PATCH", body: JSON.stringify(b) }),
  // scope: "one"=이 일정만(기본) | "series"=반복 전체 삭제
  scheduleDelete: (id: number, scope: "one" | "series" = "one") =>
    req<void>(`/schedules/${id}?scope=${scope}`, { method: "DELETE" }),
  // ── 받아쓰기(음성 → 텍스트) — /api/chat/dictation/* ──
  dictations: () => req<DictationListResult>("/dictation"),
  dictationDetail: (id: number) => req<DictationDetail>(`/dictation/${id}`),
  dictationDelete: (id: number) => req<void>(`/dictation/${id}`, { method: "DELETE" }),
  /** 전사 결과 다운로드 URL — 세션 쿠키로 인증되므로 <a href> 로 그대로 열면 된다. */
  dictationDownloadUrl: (id: number, format: "txt" | "srt" | "vtt" = "txt") =>
    `${BASE}/dictation/${id}/download?format=${format}`,
  /** 음성 업로드. 파일이 커서 진행률이 필요하므로 fetch 대신 XHR 을 쓴다. */
  dictationUpload: (file: File, onProgress?: (pct: number) => void) =>
    new Promise<DictationItem>((resolve, reject) => {
      const fd = new FormData();
      fd.append("file", file);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${BASE}/dictation`);
      xhr.withCredentials = true;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onerror = () => reject(new Error("업로드 중 연결이 끊겼습니다."));
      xhr.onload = () => {
        if (xhr.status === 401) {
          handleSessionExpired();
          reject(new UnauthorizedError("UNAUTHENTICATED"));
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as DictationItem);
          } catch {
            reject(new Error("업로드 응답을 해석하지 못했습니다."));
          }
          return;
        }
        // 413(용량)·415(형식)·503(미설정) 등은 서버가 담은 안내 문구를 그대로 보여준다.
        let msg = `업로드 실패 (${xhr.status})`;
        let code: string | undefined;
        try {
          const j = JSON.parse(xhr.responseText);
          code = j?.detail?.code;
          msg = j?.detail?.message || j?.message || msg;
        } catch {
          /* 본문 없음 — 상태코드 메시지 유지 */
        }
        if (xhr.status === 403) reject(new ForbiddenError(msg, code));
        else reject(new Error(msg));
      };
      xhr.send(fd);
    }),
  loginUrl: (redirectTo = "/") => `${BASE}/login?redirect_to=${encodeURIComponent(redirectTo)}`,
  logout: () => req<void>("/logout", { method: "POST" }),
  authConfig: () => req<AuthConfig>("/config", undefined, { public: true }),
};

export interface StreamProgress {
  phase: "map" | "reduce" | string;
  done: number;
  total: number;
  message: string;
}

export interface StreamHandlers {
  onChunk: (text: string) => void;
  onDone: (done: QueryDone) => void;
  onError: (message: string) => void;
  // 보고서 종합(SYNTH) 등 장시간 작업의 진행률(선택). 미지정이면 무시.
  onProgress?: (p: StreamProgress) => void;
}

/**
 * POST /api/chat/stream 을 fetch + ReadableStream 으로 구독한다.
 * (EventSource 는 GET 전용이라 직접 SSE 파싱)
 */
export async function streamQuery(
  question: string,
  customer: string,
  threadId: string,
  handlers: StreamHandlers,
  signal?: AbortSignal,
  modelId?: number | null, // 답변 모델(관리자 등록 id). 미지정 → 서버가 기본값 모델 적용
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${BASE}/stream`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify({
        question,
        customer,
        thread_id: threadId,
        model_id: modelId ?? undefined,
        ...getAnswerOptions(),
      }),
      signal,
    });
  } catch (e) {
    if ((e as { name?: string })?.name === "AbortError") return; // 사용자 중지 — 폴백 금지
    handlers.onError("network");
    return;
  }
  if (res.status === 401) {
    handleSessionExpired(); // 스트리밍 중 만료 → 자동 로그오프
    handlers.onError("unauthorized");
    return;
  }
  if (!res.ok || !res.body) {
    handlers.onError(`스트리밍 실패 (${res.status})`);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  const dispatch = (raw: string) => {
    const lines = raw.split("\n");
    let event = "message";
    let data = "";
    for (const line of lines) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data += line.slice(5).trim();
    }
    if (!data) return;
    let payload: unknown;
    try {
      payload = JSON.parse(data);
    } catch {
      return;
    }
    if (event === "chunk") handlers.onChunk((payload as { text: string }).text || "");
    else if (event === "done") handlers.onDone(payload as QueryDone);
    else if (event === "progress") handlers.onProgress?.(payload as StreamProgress);
    else if (event === "error") handlers.onError((payload as { message: string }).message || "error");
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      // SSE 이벤트 구분자는 빈 줄(\n\n)
      while ((idx = buf.indexOf("\n\n")) !== -1) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        if (chunk.trim()) dispatch(chunk);
      }
    }
    if (buf.trim()) dispatch(buf);
  } catch (e) {
    if ((e as { name?: string })?.name === "AbortError") return; // 사용자 중지 — 조용히 종료
    handlers.onError("stream-read");
  }
}

// 답변 본문 끝에 붙은 "📚 참고 문서\n· ..." 블록을 분리(중복 표시 방지).
// 반환: { body: 출처 제거된 본문, sources: ["· [라벨] 제목: url", ...] }
export function splitSources(text: string): { body: string; sources: string[] } {
  const marker = "📚 참고 문서";
  const idx = (text || "").indexOf(marker);
  if (idx === -1) return { body: text || "", sources: [] };
  const body = text.slice(0, idx).trimEnd();
  const tail = text.slice(idx + marker.length);
  const sources = tail
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.startsWith("·"));
  return { body, sources };
}

// "· [분야별자료] 제목: https://..." → {label,title,url}
export function parseSource(s: string) {
  const cleaned = s.replace(/^·\s*/, "");
  const m = cleaned.match(/^\[(.+?)\]\s*(.*)$/);
  const label = m ? m[1] : "";
  const rest = m ? m[2] : cleaned;
  const ci = rest.lastIndexOf(": http");
  if (ci !== -1) {
    return { label, title: rest.slice(0, ci).trim(), url: rest.slice(ci + 2).trim() };
  }
  return { label, title: rest.trim(), url: null };
}
